import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { AttackSurfaceSelection } from '@/components/AttackSurfaceSelector';
import { AttackSurfaceSelector } from '@/components/AttackSurfaceSelector';
import { StyledDatePicker } from '@/components/form/StyledDatePicker';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useGetCostEstimate } from '@/hooks/useAccountCost';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useMonitoringCountMode } from '@/hooks/useMonitoringCountMode';
import { useMSRPCalculator } from '@/hooks/useMSRPCalculator';
import {
  useGetSubscription,
  useUpdateSubscription,
} from '@/hooks/useSubscription';
import { MonitoringCountModeToggle } from '@/sections/monitoring/MonitoringCountModeToggle';
import {
  IntelligenceTierSelector,
  ScanningFrequencySelector,
  SubscriptionTierSelector,
} from '@/sections/settings/subscription/components';
import {
  MSRPPriceSection,
  ReviewSection,
  UsagePriceSection,
} from '@/sections/settings/subscription/configure/components';
import { presetRange } from '@/sections/settings/subscription/constant';
import {
  getIntelligenceTier,
  resolveIntelligenceTierId,
  ScanFrequency,
} from '@/sections/settings/subscription/constant';
import { useAuth } from '@/state/auth';
import { SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';
import { calculateAssetTotal } from '@/utils/monitoring';

interface ConfigureSubscriptionProps {
  open: boolean;
  onClose: () => void;
  customerEmail?: string;
}

export function ConfigureSubscription({
  open,
  onClose,
  customerEmail,
}: ConfigureSubscriptionProps) {
  // State management with default values
  const { isPraetorianUser } = useAuth();
  const { mode } = useMonitoringCountMode(customerEmail);

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  const [localSubscription, setLocalSubscription] =
    useState<SubscriptionConfiguration>({
      estimatedCost: 0,
      attackSurface: {
        external: false,
        applications: false,
        cloud: false,
        code: false,
        internal: false,
        users: false,
        device: false,
        llm: false,
      },
      numberOfAssets: 0,
      numberOfApplications: 0,
      numberOfLLMs: 0,
      numberOfAEVExercises: 0,
      numberOfRedTeams: 0,
      intelligenceTier: 'centurion',
      scanSchedule: 'weekly',
      selectedTier: 'professional',
      startDate: '',
      endDate: '',
    });

  const { data: subscriptionValue, status: subscriptionStatus } =
    useGetSubscription({ email: customerEmail });

  const { mutateAsync: updateSubscription, status: updateSubscriptionStatus } =
    useUpdateSubscription({ email: customerEmail });

  // Display counts - respects count mode toggle
  const { data: displayMonitoringCounts } = useGetMonitoringCounts({
    email: customerEmail,
    countType:
      mode === 'last-scan' ? 'active_monitoring_count' : 'monitoring_count',
  });

  // Pricing counts - always use 30-day rolling for consistent pricing
  const { data: pricingMonitoringCounts } = useGetMonitoringCounts({
    email: customerEmail,
    countType: 'monitoring_count',
  });

  // P&L cost data — Praetorian-only, fetched for the review step
  const [costSelectedDays, setCostSelectedDays] = useState<number | null>(28);
  const [costCustomFrom, setCostCustomFrom] = useState('');
  const [costCustomTo, setCostCustomTo] = useState('');
  const isCustomCostRange = costCustomFrom !== '' && costCustomTo !== '';
  const costRange = isCustomCostRange
    ? { from: costCustomFrom, to: costCustomTo }
    : costSelectedDays
      ? presetRange(costSelectedDays)
      : undefined;
  function handleCostPreset(days: number) {
    setCostSelectedDays(days);
    setCostCustomFrom('');
    setCostCustomTo('');
  }

  // Display total - shown in UsagePriceSection "Current Asset Usage"
  const displayTotalAssets = !displayMonitoringCounts
    ? 0
    : calculateAssetTotal(
        displayMonitoringCounts,
        localSubscription.attackSurface
      );

  // Pricing total - always 30-day rolling, used for price calculations
  const pricingTotalAssets = !pricingMonitoringCounts
    ? 0
    : calculateAssetTotal(
        pricingMonitoringCounts,
        localSubscription.attackSurface
      );

  // React 19: Simple property access doesn't need useMemo
  const actualTotalApplications =
    displayMonitoringCounts?.applications?.webseed || 0;

  // Total code repos from monitoring data
  const actualTotalCodeRepos = !displayMonitoringCounts?.code
    ? 0
    : Object.values(displayMonitoringCounts.code).reduce(
        (acc, curr) => acc + curr,
        0
      );

  // Tier-based pricing overrides
  const isFreemium = localSubscription.selectedTier === 'freemium';
  const isFoundational = localSubscription.selectedTier === 'foundational';
  // Foundational and Freemium tiers do not include web applications
  const tierIncludesWebApps = !isFreemium && !isFoundational;

  // Calculate Usage Price based on actual current usage (always 30-day rolling for consistent pricing)
  const { floor: usageFloor, msrp: usagePrice } = useMSRPCalculator({
    numberOfApplications: tierIncludesWebApps ? actualTotalApplications : 0,
    scanSchedule: localSubscription.scanSchedule,
    numberOfAssets: pricingTotalAssets,
    selectedTier: localSubscription.selectedTier,
    numberOfAEVExercises: 0,
  });

  // Calculate MSRP based on coverage limits
  const { floor, msrp, breakdown, floorBreakdown, atPlatformFloor } =
    useMSRPCalculator({
      numberOfApplications: tierIncludesWebApps
        ? localSubscription.numberOfApplications
        : 0,
      scanSchedule: localSubscription.scanSchedule,
      numberOfAssets: localSubscription.numberOfAssets,
      selectedTier: localSubscription.selectedTier,
      numberOfAEVExercises: localSubscription.numberOfAEVExercises,
      numberOfRedTeams: localSubscription.numberOfRedTeams,
    });
  // Guard Intelligence tier annual cost (legacy aiCreditPack falls back via resolver).
  // Freemium is a $0 plan that carries no paid Intelligence, so normalize the
  // EFFECTIVE display tier to `none` — otherwise the unreset default ('centurion')
  // would make Step 2 highlight Centurion and Review show "$12,000" under a $0
  // total, diverging from the `none` the save path persists. The raw
  // localSubscription.intelligenceTier is untouched, so toggling back to a paid
  // tier still restores the prior selection.
  const selectedIntelligenceTierId = isFreemium
    ? 'none'
    : resolveIntelligenceTierId(localSubscription);
  const intelligenceTierData = getIntelligenceTier(selectedIntelligenceTierId);
  const intelligenceAnnualCost = intelligenceTierData.annualPrice;

  // Minimum subscriptions (platform line at its $13K floor) must carry at least
  // Centurion ($12K). BYOAI/None is only allowed above the floor. Freemium is $0-exempt.
  const mustPurchaseIntelligence = atPlatformFloor && !isFreemium;
  const intelligenceSelectionValid =
    !mustPurchaseIntelligence || selectedIntelligenceTierId !== 'none';

  // P&L cost estimate — uses the estimate endpoint with proposed subscription params
  const aiCreditMonthly = intelligenceAnnualCost / 12;
  const { data: costData, isFetching: costLoading } = useGetCostEstimate({
    email: customerEmail,
    enabled: isPraetorianUser && currentStep === 3,
    from: costRange?.from,
    to: costRange?.to,
    acv: localSubscription.estimatedCost,
    aiCredit: aiCreditMonthly,
    customerType: 'managed',
  });

  const subscriptionMsrp = isFreemium ? 0 : msrp;
  const totalMsrp = isFreemium ? 0 : msrp + intelligenceAnnualCost;
  const subscriptionFloor = isFreemium ? 0 : floor;
  const subscriptionBreakdown = isFreemium
    ? {
        fqdnCost: 0,
        assetCost: 0,
        aevCost: 0,
        redTeamCost: 0,
      }
    : breakdown;
  const subscriptionFloorBreakdown = isFreemium
    ? {
        fqdnCost: 0,
        assetCost: 0,
        aevCost: 0,
        redTeamCost: 0,
      }
    : floorBreakdown;

  const handleSubscriptionChange = useCallback(
    (value: Partial<SubscriptionConfiguration>) => {
      setLocalSubscription(prev => ({ ...prev, ...value }));
    },
    []
  );

  const handleAttackSurfaceChange = useCallback(
    (newAttackSurface: AttackSurfaceSelection) => {
      setLocalSubscription(prev => ({
        ...prev,
        // Convert AttackSurfaceSelection to Record<string, boolean> safely
        attackSurface: { ...newAttackSurface } as Record<string, boolean>,
      }));
    },
    []
  );

  const handleAttackSurfaceSelectionChange = useCallback(
    (key: keyof AttackSurfaceSelection, selected: boolean) => {
      if (key === 'applications') {
        // When Web Applications is disabled, reset the number of applications
        // When re-enabled, restore to actual assessment count
        handleSubscriptionChange({
          numberOfApplications: selected ? actualTotalApplications : 0,
        });
      } else if (key === 'llm' && !selected) {
        // When LLM is disabled, reset numberOfLLMs to 0.
        // sanitizedSubscription already does this at save time, but clearing
        // here keeps local state consistent so the summary reflects reality.
        handleSubscriptionChange({ numberOfLLMs: 0 });
      }
    },
    [handleSubscriptionChange, actualTotalApplications]
  );

  // Memoized save handler
  const handleSave = useCallback(async () => {
    // Validate Web FQDNs when web attack surface is enabled
    if (
      localSubscription.attackSurface.applications &&
      localSubscription.numberOfApplications === 0
    ) {
      toast.error(
        '# of Web FQDNs cannot be 0 when Web attack surface is enabled'
      );
      return;
    }
    if (!intelligenceSelectionValid) {
      toast.error(
        'Minimum subscriptions must include at least Centurion ($12K in AI credits)'
      );
      setCurrentStep(2);
      return;
    }
    // Sanitize tier-irrelevant fields before saving to prevent invalid data.
    // "Advanced" tiers include web-app (DAST) coverage; "AEV" tiers include
    // Adversary Emulation exercises. ludacris mirrors Ultimate until product
    // specifies otherwise, so it belongs in both buckets.
    const isAdvancedTier =
      localSubscription.selectedTier === 'professional' ||
      localSubscription.selectedTier === 'ultimate' ||
      localSubscription.selectedTier === 'ludacris';
    const isAevTier =
      localSubscription.selectedTier === 'ultimate' ||
      localSubscription.selectedTier === 'ludacris';
    const isLudacris = localSubscription.selectedTier === 'ludacris';
    const sanitizedSubscription = {
      ...localSubscription,
      numberOfApplications: isAdvancedTier
        ? localSubscription.numberOfApplications
        : 0,
      numberOfAEVExercises: isAevTier
        ? localSubscription.numberOfAEVExercises
        : 0,
      numberOfRedTeams: isLudacris ? localSubscription.numberOfRedTeams : 0,
      numberOfLLMs: localSubscription.attackSurface.llm
        ? (localSubscription.numberOfLLMs ?? 0)
        : 0,
      estimatedCost:
        localSubscription.selectedTier === 'freemium'
          ? 0
          : localSubscription.estimatedCost,
    };

    // Persist intelligenceTier ONLY when the operator has a real, explicit value
    // for it — either they (re)selected a tier this session (including an explicit
    // "None / BYOAI" choice), or the loaded subscription already carried a real
    // intelligenceTier. For a legacy aiCreditPack-only blob, intelligenceTier stays
    // `undefined` on load, so we OMIT the key here: the persisted blob stays legacy
    // and the backend budget hook remains a no-op. We never author a tier id
    // resolved from a legacy aiCreditPack just because some unrelated field changed.
    if (localSubscription.selectedTier === 'freemium') {
      // Freemium is a $0 plan and carries no paid Intelligence. Force the tier
      // to `none` so we never persist a paid tier (e.g. the default Centurion)
      // against a $0 total — which would make SubscriptionSummary render a
      // negative platform cost (estimatedCost − intelligenceAnnualPrice).
      sanitizedSubscription.intelligenceTier = 'none';
    } else if (localSubscription.intelligenceTier !== undefined) {
      sanitizedSubscription.intelligenceTier =
        localSubscription.intelligenceTier;
    } else {
      // Ensure no resolved-from-legacy id leaks in via the spread above.
      delete (sanitizedSubscription as { intelligenceTier?: string })
        .intelligenceTier;
    }

    // Include MSRP in saved configuration if Praetorian user
    const configToSave = isPraetorianUser
      ? {
          ...sanitizedSubscription,
          msrp: subscriptionMsrp,
          intelligenceAnnualCost: getIntelligenceTier(
            resolveIntelligenceTierId(sanitizedSubscription)
          ).annualPrice,
          msrpBreakdown: subscriptionBreakdown,
          floorPrice: subscriptionFloor,
          floorBreakdown: subscriptionFloorBreakdown,
        }
      : sanitizedSubscription;
    await updateSubscription({ subscription: configToSave });
    onClose();
  }, [
    localSubscription,
    updateSubscription,
    onClose,
    isPraetorianUser,
    subscriptionMsrp,
    subscriptionBreakdown,
    subscriptionFloor,
    subscriptionFloorBreakdown,
    intelligenceSelectionValid,
  ]);

  // Sync local subscription state when server data changes
  // Using subscriptionStatus as a stable trigger - when status changes from loading to success,
  // we know we have fresh data. This avoids JSON.stringify which breaks memoization.
  useEffect(() => {
    if (subscriptionValue && subscriptionStatus === 'success') {
      // useGetSubscription returns the flattened config, but a raw configuration
      // record ({ name, value }) can also reach here; unwrap defensively so the
      // legacy aiCreditPack read-fallback resolves regardless of shape.
      const subscriptionConfig = (
        'value' in subscriptionValue
          ? (subscriptionValue as { value: SubscriptionConfiguration }).value
          : subscriptionValue
      ) as SubscriptionConfiguration;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing server data to local state on fetch success; useEffect is the correct pattern for external data sync
      setLocalSubscription(_prev => ({
        ...subscriptionConfig,
        attackSurface: {
          external: subscriptionConfig.attackSurface?.external ?? false,
          applications: subscriptionConfig.attackSurface?.applications ?? false,
          cloud: subscriptionConfig.attackSurface?.cloud ?? false,
          code: subscriptionConfig.attackSurface?.code ?? false,
          internal: subscriptionConfig.attackSurface?.internal ?? false,
          users: subscriptionConfig.attackSurface?.users ?? false,
          device: subscriptionConfig.attackSurface?.device ?? false,
          llm: subscriptionConfig.attackSurface?.llm ?? false,
        },
        startDate: subscriptionConfig.startDate || '',
        endDate: subscriptionConfig.endDate || '',
        numberOfLLMs: subscriptionConfig.numberOfLLMs ?? 0,
        numberOfAEVExercises: subscriptionConfig.numberOfAEVExercises ?? 0,
        numberOfRedTeams: subscriptionConfig.numberOfRedTeams ?? 0,
        // Preserve the ORIGINAL stored intelligenceTier (already spread above):
        // a real value stays, but a legacy aiCreditPack-only blob keeps it
        // `undefined`. Do NOT overwrite with resolveIntelligenceTierId() here —
        // that would persist a resolved-from-legacy id on the next save and
        // wrongly trigger the backend budget hook. The resolved id is used for
        // DISPLAY only (Intelligence step + Review), via resolveIntelligenceTierId.
      }));
    }
  }, [subscriptionStatus, subscriptionValue]);

  // Reset step when modal opens
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resetting wizard step on modal open; useEffect is the correct pattern for responding to prop changes
      setCurrentStep(1);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Subscription"
      size="7xl"
      baseClassName="max-h-[90vh]"
      disableAutoFocus={true}
      hideFooter
      data-testid="configure-subscription-modal"
      aria-labelledby="configure-subscription-title"
    >
      {subscriptionStatus === 'pending' && !subscriptionValue ? (
        <Spinner size="xl" centered />
      ) : (
        <>
          {/* Step indicator */}
          <div className="mb-4 flex items-center gap-2">
            {[
              { step: 1, label: 'Subscription' },
              { step: 2, label: 'Intelligence' },
              { step: 3, label: 'Review' },
            ].map(({ step, label }) => (
              <div key={step} className="flex items-center gap-2">
                {step > 1 && (
                  <div
                    className={cn(
                      'h-px w-8',
                      currentStep >= step
                        ? 'bg-chariot-brand-primary'
                        : 'bg-chariot-stroke'
                    )}
                  />
                )}
                <div className="flex items-center gap-1.5">
                  <div
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
                      currentStep === step
                        ? 'bg-chariot-brand-primary text-white'
                        : currentStep > step
                          ? 'bg-chariot-brand-primary/20 text-chariot-brand-primary'
                          : 'bg-chariot-stroke text-chariot-text-secondary'
                    )}
                  >
                    {currentStep > step ? '✓' : step}
                  </div>
                  <span
                    className={cn(
                      'text-sm font-medium',
                      currentStep === step
                        ? 'text-chariot-text-primary'
                        : 'text-chariot-text-secondary'
                    )}
                  >
                    {label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="relative h-[640px]">
            {/* Step 1: Subscription */}
            {currentStep === 1 && (
              <div
                className="absolute inset-0 space-y-3 overflow-y-auto"
                role="form"
                aria-label="Subscription configuration form"
              >
                {/* Count Mode Toggle - controls asset counting and pricing */}
                <div className="flex justify-end">
                  <MonitoringCountModeToggle
                    customerEmail={customerEmail ?? ''}
                  />
                </div>

                {/* Subscription Term + Attack Surface Coverage Row */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[auto_1fr]">
                  <section
                    className="space-y-2"
                    aria-labelledby="term-section-title"
                  >
                    <h3
                      id="term-section-title"
                      className="text-chariot-text-primary text-lg font-semibold"
                    >
                      Subscription Term
                    </h3>
                    <div className="flex items-center gap-3">
                      <label className="text-chariot-text-primary shrink-0 text-xs">
                        Start
                      </label>
                      <StyledDatePicker
                        label=""
                        name="start-date"
                        value={localSubscription.startDate || ''}
                        onChange={value =>
                          handleSubscriptionChange({ startDate: value })
                        }
                        max={localSubscription.endDate}
                        className="max-w-[160px]"
                      />
                      <label className="text-chariot-text-primary shrink-0 text-xs">
                        End
                      </label>
                      <StyledDatePicker
                        label=""
                        name="end-date"
                        value={localSubscription.endDate || ''}
                        onChange={value =>
                          handleSubscriptionChange({ endDate: value })
                        }
                        min={localSubscription.startDate}
                        className="max-w-[160px]"
                      />
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h3 className="text-chariot-text-primary text-lg font-semibold">
                      Attack Surface Coverage
                    </h3>
                    <AttackSurfaceSelector
                      variant="compact"
                      value={{
                        external:
                          localSubscription.attackSurface.external || false,
                        applications:
                          localSubscription.attackSurface.applications || false,
                        internal:
                          localSubscription.attackSurface.internal || false,
                        cloud: localSubscription.attackSurface.cloud || false,
                        code: localSubscription.attackSurface.code || false,
                        users: localSubscription.attackSurface.users || false,
                        device: localSubscription.attackSurface.device || false,
                        llm: localSubscription.attackSurface.llm || false,
                      }}
                      onChange={handleAttackSurfaceChange}
                      onSelectionChange={handleAttackSurfaceSelectionChange}
                      requiredOptions={['external']}
                    />
                  </section>
                </div>

                {/* Scanning Frequency + Subscription Tier Row */}
                <div className="grid grid-cols-1 gap-3">
                  {/* Scanning Frequency Section */}
                  <ScanningFrequencySelector
                    selectedFrequency={
                      localSubscription.scanSchedule as ScanFrequency
                    }
                    onFrequencyChange={frequency =>
                      handleSubscriptionChange({ scanSchedule: frequency })
                    }
                  />

                  {/* Subscription Tier Section */}
                  <SubscriptionTierSelector
                    selectedTier={localSubscription.selectedTier}
                    onTierChange={tier => {
                      // Professional, Ultimate, and Ludacris tiers include the web
                      // attack surface (ludacris mirrors Ultimate until product
                      // specifies otherwise)
                      const isAdvancedTier =
                        tier === 'professional' ||
                        tier === 'ultimate' ||
                        tier === 'ludacris';
                      const updatedAttackSurface = isAdvancedTier
                        ? {
                            ...localSubscription.attackSurface,
                            applications: true,
                          }
                        : {
                            ...localSubscription.attackSurface,
                            applications: false,
                          };

                      // Default application coverage to actual assessment count for advanced tiers
                      // Only set if current value is 0 (don't override saved values)
                      // Preserve values when switching to lower tiers — calculator ignores them,
                      // but they're restored if user switches back (values only reset on Save/Cancel)
                      const updatedNumberOfApplications =
                        isAdvancedTier &&
                        localSubscription.numberOfApplications === 0
                          ? actualTotalApplications
                          : localSubscription.numberOfApplications;

                      handleSubscriptionChange({
                        selectedTier: tier,
                        attackSurface: updatedAttackSurface,
                        numberOfApplications: updatedNumberOfApplications,
                      });
                    }}
                    tiers={[
                      'freemium',
                      'foundational',
                      'professional',
                      'ultimate',
                      'ludacris',
                    ]}
                  />
                </div>

                {/* Pricing Sections - Praetorian users see Usage + MSRP (no negotiated price here) */}
                {isPraetorianUser && (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* Usage Price Section */}
                    <UsagePriceSection
                      actualTotalAssets={displayTotalAssets}
                      actualTotalApplications={
                        tierIncludesWebApps ? actualTotalApplications : 0
                      }
                      actualTotalCodeRepos={actualTotalCodeRepos}
                      usagePrice={usagePrice}
                      usageFloor={usageFloor}
                    />

                    {/* MSRP Price Section */}
                    <MSRPPriceSection
                      localSubscription={localSubscription}
                      onAssetChange={value =>
                        setLocalSubscription(prev => ({
                          ...prev,
                          numberOfAssets: value,
                        }))
                      }
                      onAppChange={value =>
                        setLocalSubscription(prev => ({
                          ...prev,
                          numberOfApplications: value,
                        }))
                      }
                      onAEVExerciseChange={value =>
                        setLocalSubscription(prev => ({
                          ...prev,
                          numberOfAEVExercises: value,
                        }))
                      }
                      onRedTeamChange={value =>
                        setLocalSubscription(prev => ({
                          ...prev,
                          numberOfRedTeams: value,
                        }))
                      }
                      onLLMChange={value =>
                        setLocalSubscription(prev => ({
                          ...prev,
                          numberOfLLMs: value,
                        }))
                      }
                      msrp={subscriptionMsrp}
                      floor={subscriptionFloor}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Guard Intelligence */}
            {currentStep === 2 && (
              <div className="absolute inset-0 overflow-y-auto">
                <IntelligenceTierSelector
                  // DISPLAY-only: resolve the effective tier (new field wins;
                  // legacy aiCreditPack falls back) so the right card is
                  // highlighted even when intelligenceTier hasn't been explicitly
                  // selected this session. onTierChange writes the explicit value.
                  selectedTier={selectedIntelligenceTierId}
                  onTierChange={tier =>
                    setLocalSubscription(prev => ({
                      ...prev,
                      intelligenceTier: tier,
                    }))
                  }
                  requireMinimumTier={mustPurchaseIntelligence}
                />
              </div>
            )}

            {/* Step 3: Review & Save */}
            {currentStep === 3 && (
              <div className="absolute inset-0 space-y-4 overflow-y-auto">
                <ReviewSection
                  localSubscription={localSubscription}
                  tierIncludesWebApps={tierIncludesWebApps}
                  isPraetorianUser={isPraetorianUser}
                  totalMsrp={totalMsrp}
                  subscriptionFloor={subscriptionFloor}
                  intelligenceTierName={intelligenceTierData.name}
                  intelligenceAnnualCost={intelligenceAnnualCost}
                  subscriptionMsrp={subscriptionMsrp}
                  usagePrice={usagePrice}
                  onEstimatedCostChange={value =>
                    handleSubscriptionChange({ estimatedCost: value })
                  }
                  costWindow={costData?.windows[0]}
                  costLoading={costLoading}
                  historicalScanFrequency={costData?.scan_frequency}
                  costDateRange={{
                    selectedDays: costSelectedDays,
                    customFrom: costCustomFrom,
                    customTo: costCustomTo,
                  }}
                  onCostPreset={handleCostPreset}
                  onCostCustomFrom={v => {
                    setCostCustomFrom(v);
                    setCostSelectedDays(null);
                  }}
                  onCostCustomTo={v => {
                    setCostCustomTo(v);
                    setCostSelectedDays(null);
                  }}
                />
              </div>
            )}
          </div>

          {/* Custom footer */}
          <div className="border-chariot-stroke mt-4 flex items-center justify-between border-t pt-4">
            <button
              onClick={onClose}
              className="text-chariot-text-secondary hover:text-chariot-text-primary py-2 text-base font-medium transition-colors"
              data-testid="modal-cancel-button"
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {currentStep > 1 && (
                <Button
                  onClick={() =>
                    setCurrentStep(prev => Math.max(prev - 1, 1) as 1 | 2 | 3)
                  }
                  variant="ghost"
                  className="bg-chariot-background-primary px-4"
                  data-testid="modal-back-button"
                >
                  Back
                </Button>
              )}
              <Button
                onClick={
                  currentStep === 3
                    ? handleSave
                    : () =>
                        setCurrentStep(
                          prev => Math.min(prev + 1, 3) as 1 | 2 | 3
                        )
                }
                className="ml-2 min-w-24 px-4"
                disabled={
                  (currentStep === 3 &&
                    updateSubscriptionStatus === 'pending') ||
                  (currentStep === 2 && !intelligenceSelectionValid)
                }
                data-testid="modal-primary-button"
              >
                {currentStep === 3 && updateSubscriptionStatus === 'pending'
                  ? 'Saving...'
                  : currentStep === 3
                    ? 'Save'
                    : 'Next'}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
